// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';
import range from 'lodash/range';

import {ButtonEl} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {WarningEl} from 'common/components/warning';
import {WarningTheme} from 'common/components/warning/constants';
import {SvgIconEl} from 'common/components/svg-icon';
import {CollapsibleTheme, SectionEl} from 'common/components/section';
import {TextHighlightEl} from 'common/components/text-highlight';
import {SpinnerEl} from 'common/components/spinner';
import {QueryBlockEl} from 'common/query-builder/query-block/panel';
import {SelectorType} from 'common/query-builder/query-block/constants';
import {MenuType} from 'common/query-builder/query-block/types';
import {TooltipEl} from 'common/components/tooltip';
import {TimeQueryBlockEl} from 'common/query-builder/query-block/time-menu/panel';

import {RecordingPlayerSource} from '../recording-player/types';
import type {ProfileActivity} from './index';
import {ProfileActivityPropertiesEl} from './activity-properties';

function ViewReplaysButton({profileActivity}: {profileActivity: ProfileActivity}) {
  const {t} = profileActivity.getContext(`i18n`);

  let tooltipText: string | null = null;
  if (!profileActivity.getContext(`currentProject`).get().value?.hasIntegratedSessionReplay) {
    tooltipText = t(`session-replay.integrate-replay`, {docsLink: `https://docs.mixpanel.com/docs/session-replay`});
  } else if (profileActivity.state.loading.recordings) {
    tooltipText = t(`profile.replays-loading`);
  } else if (profileActivity.state.recordings.length === 0) {
    tooltipText = t(`profile.replays-none`);
  } else {
    tooltipText = t(`profile.watch-replays-tooltip`);
  }

  return (
    <div sel='.view-replays-button-container'>
      <ButtonEl
        sel='.control-button'
        attrs={{
          theme: ButtonTheme.Secondary,
          icon: `replay`,
          disabled: profileActivity.state.recordings.length === 0,
        }}
        on={{click: profileActivity.helpers.watchReplays}}
        elref='viewReplaysButton'
      >
        {t(`profile.watch-replays`)}
        {profileActivity.state.loading.recordings && <SpinnerEl />}
      </ButtonEl>
      <TooltipEl
        attrs={{
          text: tooltipText,
          position: `top`,
        }}
      />
    </div>
  );
}

export function template(this: ProfileActivity) {
  const isLoading =
    this.state.loading.initial || this.state.loading.eventDefinitions || this.state.loading.propertyDefinitions;
  const userActivity = this.helpers.getUserActivityDisplay();
  const {t, print} = this.getContext(`i18n`);

  return (
    <div sel='.profile-activity' class={{'sidebar-mode': this.attr(`sidebar-mode`)}}>
      {!this.attr(`sidebar-mode`) && (
        <div sel='.title-bar'>
          <div sel='.activity-title'>{t(`profile.activity-feed`)}</div>
          <div sel='.filters-section'>
            {this.helpers.isSessionRecordingEnabled() && !this.attr(`data-group-id`) && (
              <ViewReplaysButton profileActivity={this} />
            )}
            <ButtonEl
              sel='.control-button'
              attrs={{
                theme: ButtonTheme.Secondary,
                icon: `line`,
              }}
              on={{click: this.helpers.viewInInsights}}
            >
              {t(`profile.view-in-insights`)}
            </ButtonEl>
            <TimeQueryBlockEl
              maxDataHistoryDays={this.attr(`history-limit-days`) ?? undefined}
              selected={this.state.dateRange}
              selectorType={SelectorType.Button}
              leftIcon='calendar'
              trackName={this.helpers.REPORT_NAME}
              projectAsOfDate={this.attr(`project-as-of-date`) ?? undefined}
              onSelect={this.helpers.handleDateRangeSelected}
              onTrack={this.helpers.handleQueryBuilderBlockTrack}
            />
            <QueryBlockEl
              type={MenuType.Events}
              excludeCustomEvents
              dataGroupId={this.attr(`data-group-id`).toString()}
              multiple
              projectId={this.helpers.getProjectId()}
              selected={this.helpers.getSelectedHiddenEvents()}
              inverted={this.state.includedEvents.length > 0}
              includeHidden
              selectorIcon='eye-hide'
              selectorType={SelectorType.Button}
              showAllEvents
              trackName={this.helpers.REPORT_NAME}
              workspaceId={this.helpers.getWorkspaceId()}
              onSelect={this.helpers.updateEventFilter}
              onTrack={this.helpers.handleQueryBuilderBlockTrack}
            />
          </div>
        </div>
      )}
      {!this.state.error && isLoading && (
        <div sel='.mp-panel-section'>
          {range(10).map((i) => (
            <div sel='.mp-skeleton-header' key={i} />
          ))}
        </div>
      )}
      {this.state.error && !isLoading && (
        <WarningEl
          attrs={{
            'body-text': `We were unable to display this user's activity due to an error. Please try again later.`,
            'header-text': `Unable to Display Activity Feed`,
            theme: WarningTheme.Error,
          }}
        />
      )}
      {!this.state.error && !isLoading && (
        <>
          {userActivity.length === 0 && (
            <div sel='.no-data-wrapper'>
              <div sel='.no-data-image' />
              {t(`profile.no-activity`)} ({this.state.dateRange.getDisplayText()})
            </div>
          )}
          {userActivity.length > 0 && (
            <>
              {userActivity.map((dayGroup) => (
                <div sel='.day-header'>
                  <SvgIconEl
                    attrs={{
                      icon: `calendar`,
                    }}
                  />
                  {print(dayGroup.dayHeader)}
                  <div sel='.day-activities'>
                    {dayGroup.activities.map((activity) => {
                      const {eventName, eventGroup} = activity;
                      const {time, insertId, replayId} = eventGroup[0];
                      // Use $insert_id as a unique identifier for if this grouping has been rendered before
                      const hasBeenRendered = this.state.renderedGroupings.has(insertId);
                      return (
                        <div sel='.activity-event-wrapper'>
                          <div sel='.activity-time'>{print(this.helpers.getDisplayTime(time))}</div>
                          <div sel='.activity-name'>
                            <SectionEl
                              attrs={{
                                'enable-row-click': true,
                                'is-closed': true,
                                theme: CollapsibleTheme.INLINE,
                              }}
                              on={{
                                change: (ev) => this.helpers.handleExpandGrouping(ev, activity),
                              }}
                            >
                              <div sel='.activity-event-title' attrs={{slot: `title`}}>
                                {eventName}
                                {eventGroup.length > 1 && <TextHighlightEl attrs={{text: eventGroup.length}} />}
                                {this.helpers.isSessionRecordingEnabled() && replayId && (
                                  <SvgIconEl
                                    attrs={{icon: `single-replay`}}
                                    on={{
                                      click: (ev) => {
                                        ev.stopPropagation();
                                        this.showRecordingsWithMetadataAsync(
                                          [replayId],
                                          [this.attr(`distinct-id`)],
                                          RecordingPlayerSource.UsersProfile,
                                          !this.attr(`sidebar-mode`),
                                        );
                                      },
                                    }}
                                  />
                                )}
                              </div>
                              {hasBeenRendered && eventGroup.length === 1 && (
                                <div sel='.detail-pane'>
                                  <ProfileActivityPropertiesEl
                                    attrs={{
                                      'utc-offset': this.attr(`utc-offset`),
                                      'mixpanel-properties': eventGroup[0].mixpanelProperties,
                                      'user-defined-event-properties': eventGroup[0].userDefinedEventProperties,
                                      'event-name': eventName,
                                      time,
                                    }}
                                  />
                                </div>
                              )}
                              {hasBeenRendered && eventGroup.length > 1 && (
                                <div sel='.event-group-pane'>
                                  <EventGroup
                                    eventGroup={eventGroup}
                                    getDisplayTime={this.helpers.getDisplayTime}
                                    trackCollapsibleChange={this.helpers.trackCollapsibleChange}
                                    utcOffset={this.attr(`utc-offset`)}
                                    i18n={this.getContext(`i18n`)}
                                  />
                                </div>
                              )}
                            </SectionEl>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              <div sel='.activity-footer'>
                <div sel='.since-date-wrapper'>Since {this.helpers.getLastVisibleFromDate()}</div>
                <div sel='.show-more-wrapper'>
                  {this.state.loading.moreEvents ? (
                    <SpinnerEl />
                  ) : this.state.enableShowMoreButton ? (
                    <ButtonEl attrs={{theme: ButtonTheme.Secondary}} on={{click: this.helpers.handleShowMore}}>
                      {t(`profile.show-more`)}
                    </ButtonEl>
                  ) : null}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function EventGroup({eventGroup, getDisplayTime, trackCollapsibleChange, utcOffset, i18n}) {
  const {print} = i18n;

  return eventGroup.map(({mixpanelProperties, eventDisplayName, time, userDefinedEventProperties}) => (
    <div sel='.grouped-event-row'>
      <div sel='.activity-ungrouped-time'>{print(getDisplayTime(time))}</div>
      <div sel='.activity-ungrouped-name'>
        <SectionEl
          attrs={{
            'enable-row-click': true,
            'hide-caret': true,
            'is-closed': true,
            theme: CollapsibleTheme.INLINE,
          }}
          on={{
            change: (ev) => trackCollapsibleChange(ev, eventDisplayName, time, false),
          }}
        >
          <div sel='.expand-event-properties' attrs={{slot: `title`}}>
            {eventDisplayName}
          </div>
          <div sel='.sub-event-pane'>
            <ProfileActivityPropertiesEl
              attrs={{
                'utc-offset': utcOffset,
                'event-name': eventDisplayName,
                'mixpanel-properties': mixpanelProperties,
                'user-defined-event-properties': userDefinedEventProperties,
                time,
              }}
            />
          </div>
        </SectionEl>
      </div>
    </div>
  ));
}
